import Gate from "components/Gate";
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  Card,
  CardContent,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot, timelineItemClasses,
} from "@mui/lab";
import {
  PlayArrowOutlined,
  PauseCircleOutlined,
  CalendarTodayOutlined,
  EmailOutlined,
  ReplayOutlined,
  AccessTimeOutlined,
  CheckCircle,
  Warning,
  Schedule,
  RadioButtonUnchecked,
  Add,
  Description,
  ClearOutlined,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { format, parseISO, differenceInDays, subDays, addDays, startOfDay } from "date-fns";
import { parseScheduleDateLocal, getScheduleDatePart } from "../../modules/disconnectSchedules/utils/dateUtils";
import { useTheme } from "@mui/material/styles";
import { getChipVariant } from "../../utils/chipStyles";
import { getDisplayCreatorName } from "../../utils/noteUtils";
import { LeftSidebar as NavFrame } from "layout-blueprints";
import StandardHeader from "../../components/global/StandardHeader";
import { getAccountDisconnectForQueueByUuid, rescheduleAccountDisconnect, markDisconnectAsPending, resendDisconnectNotice, revertAccountDisconnect, deleteByUuid } from "../../modules/accountDisconnects/api/actions";
import { post as createDisconnectHold } from "../../modules/disconnectHolds/api/actions";
import { formatMoney } from "../../setup/helpers";
import axios from "axios";
import { setSnackbar } from "../../modules/snackbar/api/snackbar";
import { useDispatch } from "react-redux";

const DisconnectDetail = (props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const [holdModalOpen, setHoldModalOpen] = useState(false);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [revertModalOpen, setRevertModalOpen] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [holdNote, setHoldNote] = useState("");
  const [newNote, setNewNote] = useState("");
  const [noteDate, setNoteDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [systemNotes, setSystemNotes] = useState([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [letterUrls, setLetterUrls] = useState({});

  const parseDate = (dateValue) => {
    if (!dateValue) return null;
    if (dateValue instanceof Date) return dateValue;
    if (typeof dateValue === 'string') {
      try {
        return parseISO(dateValue);
      } catch {
        return new Date(dateValue);
      }
    }
    return new Date(dateValue);
  };

  const disconnectData = props.accountDisconnects?.disconnectQueue?.queueItem?.data || {};
  const isLoading = props.accountDisconnects?.disconnectQueue?.queueItem?.isLoading || false;
  const disconnectId = disconnectData.id || disconnectData.disconnectId || "N/A";

  const fetchSystemNotes = async () => {
    if (!disconnectId || disconnectId === "N/A") return;
    
    setIsLoadingNotes(true);
    try {
      const response = await axios.get(`/accountdisconnectnote?accountDisconnectId=${disconnectId}`);
      setSystemNotes(response.data || []);
    } catch (error) {
      console.error("Error fetching notes:", error);
      setSystemNotes([]);
    } finally {
      setIsLoadingNotes(false);
    }
  };

  useEffect(() => {
    if (uuid) {
      props.getAccountDisconnectForQueueByUuid(uuid);
    }
    document.title = "Disconnect Detail";
  }, [uuid]);

  // Fetch notes when disconnectId becomes available
  useEffect(() => {
    if (disconnectId && disconnectId !== "N/A") {
      fetchSystemNotes();
    }
  }, [disconnectId]);

  useEffect(() => {
    const fetchLetterUrls = async () => {
      const urls = {};
      const letters = [
        { key: "14-day-notice", id: disconnectData.letter14DayId },
        { key: "10-day-notice", id: disconnectData.letter10DayId },
        { key: "final-notice", id: disconnectData.letterFinalNoticeId },
        { key: "disconnect-letter", id: disconnectData.letterDisconnectId },
      ];

      for (const letter of letters) {
        if (letter.id) {
          try {
            const res = await axios.get(`/letters/${letter.id}`);
            if (res.data && res.data.url) {
              urls[letter.key] = res.data.url;
            }
          } catch (err) {
            console.error(`Error fetching letter URL for ${letter.key}:`, err);
          }
        }
      }
      setLetterUrls(urls);
    };

    if (
      disconnectData.letter14DayId ||
      disconnectData.letter10DayId ||
      disconnectData.letterFinalNoticeId ||
      disconnectData.letterDisconnectId
    ) {
      fetchLetterUrls();
    }
  }, [
    disconnectData.letter14DayId,
    disconnectData.letter10DayId,
    disconnectData.letterFinalNoticeId,
    disconnectData.letterDisconnectId,
  ]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    if (!disconnectId || disconnectId === "N/A") {
      dispatch(setSnackbar(true, "error", "Disconnect ID not found"));
      return;
    }

    try {
      await axios.post(`/accountdisconnectnote`, {
        accountDisconnectId: disconnectId,
        note: newNote.trim(),
        priority: 1,
      });
      dispatch(setSnackbar(true, "success", "Note added successfully"));
      setNoteModalOpen(false);
      setNewNote("");
      setNoteDate(format(new Date(), "yyyy-MM-dd"));
      fetchSystemNotes();
    } catch (error) {
      console.error("Error adding note:", error);
      dispatch(setSnackbar(true, "error", error.response?.data?.message || "Failed to add note"));
    }
  };
  const address = disconnectData.address || disconnectData.address1 || "N/A";
  const accountNum = disconnectData.accountNum || "N/A";
  const customerName = disconnectData.customerName || (disconnectData.firstName && disconnectData.lastName
    ? `${disconnectData.firstName} ${disconnectData.lastName}`
    : "N/A");
  const meterNum = disconnectData.meterNum || "N/A";
  const groupName = disconnectData.propertyGroupName || "N/A";
  const groupBgColorTag = disconnectData.propertyGroupBgColorTag;
  const balance = disconnectData.accountBalance || disconnectData.balance || 0;
  const scheduledDate = disconnectData.disconnectDate || disconnectData.scheduledDisconnectDate;
  const parsedScheduledDate = parseScheduleDateLocal(scheduledDate);
  const reason = disconnectData.reason || disconnectData.disconnectReason || "Non-payment";
  const status = disconnectData.queueStatus || disconnectData.AccountStatus || "Unknown";

  const daysInProcess = parsedScheduledDate
    ? differenceInDays(new Date(), parsedScheduledDate)
    : 0;

  // timeline stages
  const has14DayNotice = disconnectData.letter14DayId != null;
  const has10DayNotice = disconnectData.letter10DayId != null;
  const hasFinalNotice = disconnectData.letterFinalNoticeId != null;
  const hasDisconnectLetter = disconnectData.letterDisconnectId != null;
  const isDisconnected = disconnectData.disconnected === 1 || disconnectData.disconnected === true;
  const isCompleted = disconnectData.completed === 1 || disconnectData.completed === true;
  const isClosed = isCompleted && disconnectData.notes && disconnectData.notes.includes('Manually Closed');

  // Get letter send dates if available (use getScheduleDatePart to avoid timezone shift)
  const letter14DayDate = disconnectData.letter14DaySendDate 
    ? getScheduleDatePart(disconnectData.letter14DaySendDate)
    : (has14DayNotice && parsedScheduledDate ? format(subDays(parsedScheduledDate, 14), "yyyy-MM-dd") : null);
  
  const letter10DayDate = disconnectData.letter10DaySendDate
    ? getScheduleDatePart(disconnectData.letter10DaySendDate)
    : (has10DayNotice && parsedScheduledDate ? format(subDays(parsedScheduledDate, 10), "yyyy-MM-dd") : null);
  
  const letterFinalNoticeDate = disconnectData.letterFinalNoticeSendDate
    ? getScheduleDatePart(disconnectData.letterFinalNoticeSendDate)
    : (hasFinalNotice && parsedScheduledDate ? format(subDays(parsedScheduledDate, 3), "yyyy-MM-dd") : null);
  
  const letterDisconnectDate = disconnectData.letterDisconnectSendDate
    ? getScheduleDatePart(disconnectData.letterDisconnectSendDate)
    : (hasDisconnectLetter && parsedScheduledDate ? format(parsedScheduledDate, "yyyy-MM-dd") : null);

  // Determine current stage based on data (matching backend flow)
  let currentStageId;
  if (isCompleted) {
    // If completed (paid), the process was resolved before disconnection
    currentStageId = "completed";
  } else if (isDisconnected) {
    // If disconnected, we're at the disconnected stage
    currentStageId = "disconnected";
  } else if (hasDisconnectLetter) {
    // Disconnect letter sent, waiting for actual disconnection
    currentStageId = "disconnect-letter";
  } else if (hasFinalNotice) {
    // Final notice sent, waiting for disconnect letter
    currentStageId = "final-notice";
  } else if (has10DayNotice) {
    // 10 day notice sent, waiting for final notice
    currentStageId = "10-day-notice";
  } else if (has14DayNotice) {
    // 14 day notice sent, waiting for 10 day notice
    currentStageId = "14-day-notice";
  } else {
    // No notices sent yet, at the beginning
    currentStageId = "14-day-notice";
  }

  // Build timeline stages
  // "skipped" = step was not completed but a later step was (e.g., 10-day skipped, went straight to final)
  const timelineStages = [
    {
      id: "14-day-notice",
      title: "14 Day Notice",
      description: "Initial disconnection notice sent via mail",
      date: letter14DayDate,
      status: has14DayNotice ? "completed" : (currentStageId === "14-day-notice" ? "current"
        : (has10DayNotice || hasFinalNotice || hasDisconnectLetter || isDisconnected || isCompleted) ? "skipped" : "pending"),
      icon: <EmailOutlined />,
    },
    {
      id: "10-day-notice",
      title: "10 Day Notice",
      description: "Second notice with updated balance",
      date: letter10DayDate,
      status: has10DayNotice ? "completed" : (currentStageId === "10-day-notice" ? "current"
        : (hasFinalNotice || hasDisconnectLetter || isDisconnected || isCompleted) ? "skipped" : "pending"),
      icon: <EmailOutlined />,
    },
    {
      id: "final-notice",
      title: "Final Notice",
      description: "Final notice before disconnection",
      date: letterFinalNoticeDate,
      status: hasFinalNotice ? "completed" : (currentStageId === "final-notice" ? "current"
        : (hasDisconnectLetter || isDisconnected || isCompleted) ? "skipped" : "pending"),
      icon: <Warning />,
    },
    {
      id: "disconnect-letter",
      title: "Disconnect Letter",
      description: "Disconnect letter sent to account",
      date: letterDisconnectDate,
      status: hasDisconnectLetter ? "completed" : (currentStageId === "disconnect-letter" ? "current"
        : (isDisconnected || isCompleted) ? "skipped" : "pending"),
      icon: <Warning />,
    },
    {
      id: "disconnected",
      title: "Disconnection Complete",
      description: "Service disconnected",
      date: isDisconnected && disconnectData.reconnectDate 
        ? getScheduleDatePart(disconnectData.reconnectDate)
        : (isDisconnected && parsedScheduledDate ? format(parsedScheduledDate, "yyyy-MM-dd") : null),
      status: isDisconnected ? "completed" : (currentStageId === "disconnected" ? "current"
        : isCompleted ? "skipped" : "pending"),
      icon: <RadioButtonUnchecked />,
    },
  ];

  // If completed (paid), add a completed/resolved stage
  if (isCompleted) {
    const completedDescription = disconnectData.notes?.trim()
      ? disconnectData.notes.trim()
      : "Disconnection completed/resolved";
    timelineStages.push({
      id: "completed",
      title: isClosed ? "Disconnect Closed" : "Process Completed",
      description: completedDescription,
      date: disconnectData?.updated ? format(new Date(disconnectData.updated), "yyyy-MM-dd") : null,
      status: "completed",
      icon: <CheckCircle />,
    });
  }

  const getTimelineIcon = (stage) => {
    switch (stage.status) {
      case "completed":
        return <CheckCircle sx={{ color: theme.palette.success.main }} />;
      case "current":
        return <Schedule sx={{ color: theme.palette.primary.main }} />;
      case "skipped":
        return <RadioButtonUnchecked sx={{ color: theme.palette.grey[300] }} />;
      case "pending":
        return <RadioButtonUnchecked sx={{ color: theme.palette.grey[400] }} />;
      default:
        return stage.icon;
    }
  };

  const getStatusChip = (status) => {
    const normalizedStatus = status?.toLowerCase().replace(/\s+/g, "");
    let chipProps;

    switch (normalizedStatus) {
      case "scheduled":
        chipProps = {
          variant: "filled",
          sx: {
            backgroundColor: theme.palette.grey[200],
            color: theme.palette.grey[800],
            fontWeight: 500,
          },
        };
        break;
      case "active":
        chipProps = getChipVariant("success", theme);
        break;
      case "inactive":
        chipProps = getChipVariant("default", theme);
        break;
      case "closed":
        chipProps = getChipVariant("default", theme);
        break;
      default:
        chipProps = getChipVariant("default", theme);
    }

    return <Chip label={status || "Unknown"} {...chipProps} />;
  };

  const handleCloseDisconnect = async () => {
    if (!uuid) {
      dispatch(setSnackbar(true, "error", "Disconnect UUID not found"));
      return;
    }
    try {
      await props.deleteByUuid(uuid, false);
      dispatch(setSnackbar(true, "success", "Disconnect closed successfully"));
      setCloseModalOpen(false);
      navigate(-1);
    } catch (error) {
      console.error("Error closing disconnect:", error);
      dispatch(setSnackbar(true, "error", error.message || "Failed to close disconnect"));
    }
  };

  const getRunNowType = () => {
    const stage = getCurrentStage();
    if (stage === "14-day" || stage === "10-day") {
      return "final_ready";
    }
    return "";
  };

  const getRunNowLabel = () => {
    const stage = getCurrentStage();
    if (stage === "14-day" || stage === "10-day") {
      return "Generate Final Notice";
    }
    return "Process Disconnect";
  };

  const handleRunNow = async () => {
    if (!uuid) {
      dispatch(setSnackbar(true, "error", "Disconnect UUID not found"));
      return;
    }

    const pendingType = getRunNowType();
    const actionLabel = getRunNowLabel();

    try {
      await props.markDisconnectAsPending(uuid, null, false, pendingType);
      dispatch(setSnackbar(true, "success", `${actionLabel} - marked as pending successfully`));
      
      props.getAccountDisconnectForQueueByUuid(uuid);
    } catch (error) {
      console.error("Error marking disconnect as pending:", error);
      dispatch(setSnackbar(true, "error", error.message || "Failed to mark disconnect as pending"));
    }
  };

  const handleHold = () => {
    setHoldModalOpen(true);
  };

  const handleReschedule = () => {
    setRescheduleModalOpen(true);
  };

  const handleRescheduleSubmit = async () => {
    if (!uuid) {
      dispatch(setSnackbar(true, "error", "Disconnect UUID not found"));
      return;
    }

    try {
      await props.rescheduleAccountDisconnect(uuid);
      
      dispatch(setSnackbar(true, "success", "Disconnection rescheduled successfully"));
      setRescheduleModalOpen(false);
      
      props.getAccountDisconnectForQueueByUuid(uuid);
    } catch (error) {
      console.log("Error rescheduling disconnect:", error);
      dispatch(setSnackbar(true, "error", error.message || "Failed to reschedule disconnection"));
    }
  };

  const handleResendNotice = async () => {
    if (!uuid) {
      dispatch(setSnackbar(true, "error", "Disconnect UUID not found"));
      return;
    }

    try {
      await props.resendDisconnectNotice(uuid);
      dispatch(setSnackbar(true, "success", "Notice resent successfully"));
      
      // Refresh the disconnect data
      props.getAccountDisconnectForQueueByUuid(uuid);
    } catch (error) {
      console.error("Error resending notice:", error);
      dispatch(setSnackbar(true, "error", error.message || "Failed to resend notice"));
    }
  };

  const getCurrentStage = () => {
    if (!disconnectData) return null;
    
    // Determine current stage
    if (disconnectData.letterDisconnectId) {
      return "Disconnected";
    } else if (disconnectData.letterFinalNoticeId) {
      return "Final Notice";
    } else if (disconnectData.letter10DayId) {
      return "10-day";
    } else if (disconnectData.letter14DayId) {
      return "14-day";
    }
    return null;
  };

  const getPreviousStage = () => {
    if (!disconnectData) return null;
    
    // Determine current stage and return target stage to revert to
    // Customers can get both 10-day and 14-day letters, so check 10-day first (more recent)
    // Backend logic:
    // - newStatus="Final" → deletes letterDisconnectId
    // - newStatus="10-day" → deletes letterFinalNoticeId 
    // - newStatus="14-day" → deletes letterFinalNoticeId 
    
    // Check in order of most recent to least recent
    if (disconnectData.letterDisconnectId) {
      // Current: Disconnected → Target: Final
      // Backend: newStatus="Final" deletes letterDisconnectId
      return "Final";
    } else if (disconnectData.letterFinalNoticeId) {
      // Current: Final → Target: 10-day or 14-day (check which letter exists)
      // Check 10-day first since customers can get both letters, and 10-day is more recent
      // Backend: newStatus="10-day" or "14-day" deletes letterFinalNoticeId
      if (disconnectData.letter10DayId) {
        return "10-day";
      } else if (disconnectData.letter14DayId) {
        return "14-day";
      }
      // Should not happen, but handle gracefully
      return null;
    } else if (disconnectData.letter10DayId) {
      // Current: 10-day → Cannot revert (earliest stage for 10-day path)
      return null;
    } else if (disconnectData.letter14DayId) {
      // Current: 14-day → Cannot revert (earliest stage for 14-day path)
      return null;
    }
    return null;
  };

  const handleRevert = () => {
    const previousStage = getPreviousStage();
    if (!previousStage) {
      const currentStage = getCurrentStage();
      if (currentStage === "14-day" || currentStage === "10-day") {
        dispatch(setSnackbar(true, "error", `Cannot revert - already at the earliest stage (${currentStage})`));
      } else {
        dispatch(setSnackbar(true, "error", `Cannot revert from ${currentStage || "unknown stage"}`));
      }
      return;
    }
    setRevertModalOpen(true);
  };

  const handleRevertSubmit = async () => {
    if (!uuid) {
      dispatch(setSnackbar(true, "error", "Disconnect UUID not found"));
      return;
    }

    // Determine previous stage
    const previousStage = getPreviousStage();
    if (!previousStage) {
      const currentStage = getCurrentStage();
      if (currentStage === "14-day" || currentStage === "10-day") {
        dispatch(setSnackbar(true, "error", `Cannot revert - already at the earliest stage (${currentStage})`));
      } else {
        dispatch(setSnackbar(true, "error", `Cannot revert from ${currentStage || "unknown stage"}`));
      }
      setRevertModalOpen(false);
      return;
    }

    try {
      await props.revertAccountDisconnect(uuid, previousStage);
      dispatch(setSnackbar(true, "success", "Disconnect reverted to previous stage successfully"));
      setRevertModalOpen(false);
      
      // Refresh the disconnect data
      props.getAccountDisconnectForQueueByUuid(uuid);
    } catch (error) {
      console.error("Error reverting disconnect:", error);
      dispatch(setSnackbar(true, "error", error.message || "Failed to revert disconnect"));
    }
  };

  const handleHoldSubmit = async () => {
    if (!holdNote.trim()) return;
    
    if (!disconnectData || !disconnectData.accountId) {
      dispatch(setSnackbar(true, "error", "Account information not found"));
      return;
    }

    try {
      // Format expiresAt as YYYY-MM-DD HH:mm:ss (same format as create hold modal)
      const expiresAtDate = startOfDay(addDays(new Date(), 30));
      const expiresAt = format(expiresAtDate, "yyyy-MM-dd HH:mm:ss");

      // Use the same format as the create hold modal
      const holdData = {
        holdType: 'ACCOUNT',
        accountId: disconnectData.accountId,
        reason: holdNote.trim(),
        notes: holdNote.trim(),
        expiresAt: expiresAt,
        isActive: 1,
        liftReason: '',
      };

      await props.createDisconnectHold(holdData);
      
      dispatch(setSnackbar(true, "success", "Disconnection held successfully"));
      setHoldModalOpen(false);
      setHoldNote("");
      
      // Refresh the disconnect data
      if (uuid) {
        props.getAccountDisconnectForQueueByUuid(uuid);
      }
    } catch (error) {
      console.error("Error holding disconnect:", error);
      dispatch(setSnackbar(true, "error", error.message || "Failed to hold disconnection"));
    }
  };

  if (isLoading) {
    return (
      <NavFrame page={"Disconnect Detail"}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <CircularProgress />
        </Box>
      </NavFrame>
    );
  }

  return (
    <NavFrame page={"Disconnect Detail"}>
      <StandardHeader
        title={`Disconnection #${disconnectId}`}
        appPath="Disconnect Command Center"
      />
      <Box sx={{ maxWidth: "1200px", margin: "0 auto", width: "100%", p: 3 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
                  Disconnection #{disconnectId}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  {address}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
                {getStatusChip(status)}
                {disconnectData.manualHold && (
                  <Chip
                    label={`Manual Hold: ${format(parseDate(disconnectData.manualHold), "MM/dd/yyyy")}`}
                    icon={<CalendarTodayOutlined />}
                    color="primary"
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.875rem",
                      height: "32px",
                    }}
                  />
                )}
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Account
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: "medium",
                    cursor: "pointer",
                    color: "primary.main",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                  onClick={() => {
                    if (accountNum && accountNum !== "N/A") {
                      window.open(`/accountmanager/${accountNum}`, "_blank");
                    }
                  }}
                >
                  {accountNum}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Customer
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                  {customerName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Group
                </Typography>
                <Chip
                  label={groupName}
                  size="small"
                  sx={{
                    backgroundColor: groupBgColorTag 
                      ? `#${groupBgColorTag}` 
                      : undefined,
                    color: groupBgColorTag ? "#ffffff" : undefined,
                    fontWeight: "medium",
                    mt: 0.5,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Meter Number
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                  {meterNum}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="h5" sx={{ color: "error.main", fontWeight: "bold" }}>
                  {formatMoney(balance)}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Current Balance
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  {parsedScheduledDate ? format(parsedScheduledDate, "yyyy-MM-dd") : "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Scheduled Date
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  {daysInProcess}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Days in Process
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  {reason}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Disconnection Reason
                </Typography>
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}>
              <Gate capability="disconnects">
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayArrowOutlined />}
                onClick={handleRunNow}
                disabled={isCompleted || disconnectData.effectiveHoldStatus === 1 || disconnectData.batchPending === 1}
                sx={{ textTransform: "none" }}
              >
                {getRunNowLabel()}
              </Button>
              </Gate>
              <Gate capability="disconnects">
              <Button
                variant="outlined"
                startIcon={<PauseCircleOutlined />}
                onClick={handleHold}
                disabled={isCompleted || disconnectData.effectiveHoldStatus === 1}
                sx={{ textTransform: "none" }}
              >
                Hold Disconnection
              </Button>
              </Gate>
              <Gate capability="disconnects">
              <Button
                variant="outlined"
                startIcon={<CalendarTodayOutlined />}
                onClick={handleReschedule}
                disabled={isCompleted}
                sx={{ textTransform: "none" }}
              >
                Reschedule
              </Button>
              </Gate>
              <Gate capability="disconnects">
              <Button
                variant="outlined"
                startIcon={<EmailOutlined />}
                onClick={handleResendNotice}
                disabled={isCompleted}
                sx={{ textTransform: "none" }}
              >
                Resend Notice
              </Button>
              </Gate>
              <Gate capability="disconnects">
              <Button
                variant="outlined"
                startIcon={<ReplayOutlined />}
                onClick={handleRevert}
                disabled={isCompleted}
                sx={{ textTransform: "none" }}
              >
                Revert Stage
              </Button>
              </Gate>
              <Gate capability="disconnects">
              <Button
                variant="outlined"
                startIcon={<ClearOutlined />}
                onClick={() => setCloseModalOpen(true)}
                disabled={isCompleted}
                sx={{ textTransform: "none" }}
              >
                Close Disconnect
              </Button>
              </Gate>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center",  mb: 3 }}>
              <AccessTimeOutlined sx={{ mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Disconnection Timeline
              </Typography>
            </Box>
              <Timeline
                sx={{
                  [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: { xs: 0, sm: 3 },
                  },
                  [`& .${timelineItemClasses.root}`]: {
                    minHeight: { xs: "auto", sm: "120px" },
                    paddingBottom: { xs: 2, sm: 0 },
                  },
                }}
              >
                {timelineStages.map((stage, index) => (
                  <TimelineItem key={stage.id}>
                    <TimelineSeparator>
                      <TimelineDot
                        variant="outlined"
                        sx={{
                          backgroundColor: "transparent",
                          borderColor: "transparent",
                          color: (stage.status === "pending" || stage.status === "skipped") ? theme.palette.grey[400] : "white",
                          width: { xs: 40, sm: 48 },
                          height: { xs: 40, sm: 48 },
                          minWidth: { xs: 40, sm: 48 },
                          padding: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "& svg": {
                            fontSize: { xs: "1.5rem", sm: "2rem" },
                          },
                        }}
                      >
                        {getTimelineIcon(stage)}
                      </TimelineDot>
                      {index < timelineStages.length - 1 && (
                        <TimelineConnector
                          sx={{
                            minHeight: { xs: 40, sm: 60 },
                          }}
                        />
                      )}
                    </TimelineSeparator>
                    <TimelineContent
                      sx={{
                        textAlign: "left",
                        px: { xs: 1, sm: 2 },
                        py: { xs: 0, sm: 1 },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          justifyContent: { xs: "flex-start", sm: "center" },
                          alignItems: { xs: "flex-start", sm: "center" },
                          mb: 1,
                          gap: 1,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: "medium",
                              fontSize: { xs: "1rem", sm: "1.25rem" },
                              ...(letterUrls[stage.id] ? {
                                cursor: "pointer",
                                color: "primary.main",
                                "&:hover": { textDecoration: "underline" },
                              } : {}),
                            }}
                            onClick={letterUrls[stage.id] ? () => window.open(letterUrls[stage.id]) : undefined}
                          >
                            {stage.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: "0.875rem", sm: "0.875rem" },
                              mt: 0.5,
                            }}
                          >
                            {stage.description}
                          </Typography>
                          {stage.date && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5, display: "block" }}
                            >
                              {stage.date}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{flex: 1}}>
                          <Chip
                            label={stage.status}
                            size="small"
                            sx={{
                              backgroundColor:
                                stage.status === "completed"
                                  ? theme.palette.success.dark
                                  : stage.status === "current"
                                    ? theme.palette.primary.light
                                    : theme.palette.grey[200],
                              color:
                                stage.status === "completed"
                                  ? theme.palette.common.white
                                  : stage.status === "current"
                                    ? theme.palette.primary.contrastText
                                    : stage.status === "skipped"
                                      ? theme.palette.grey[500]
                                      : theme.palette.grey[700],
                              fontSize: {xs: "0.7rem", sm: "0.75rem"},
                              height: {xs: 20, sm: 24},
                            }}
                          />
                        </Box>
                      </Box>
                      {(stage.status === "current" || letterUrls[stage.id]) && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            gap: 1,
                            mt: 2,
                            width: { xs: "100%", sm: "auto" },
                          }}
                        >
                          {stage.status === "current" && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AccessTimeOutlined />}
                              sx={{
                                textTransform: "none",
                                width: { xs: "100%", sm: "auto" },
                              }}
                            >
                              Extend Deadline
                            </Button>
                          )}
                          {letterUrls[stage.id] && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Description />}
                              onClick={() => window.open(letterUrls[stage.id])}
                              sx={{
                                textTransform: "none",
                                width: { xs: "100%", sm: "auto" },
                              }}
                            >
                              View Letter
                            </Button>
                          )}
                        </Box>
                      )}
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                System Notes
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setNoteModalOpen(true)}
                sx={{ textTransform: "none" }}
              >
                Add Note
              </Button>
            </Box>

            {isLoadingNotes ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : systemNotes.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
                No system notes available
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {systemNotes
                  .sort((a, b) => new Date(b.created) - new Date(a.created))
                  .map((note) => (
                    <Paper key={note.id} sx={{ p: 2 }}>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Avatar sx={{ width: 40, height: 40 }}>
                          {getDisplayCreatorName(note.creatorName)?.charAt(0) || "S"}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                              {getDisplayCreatorName(note.creatorName)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(parseDate(note.created), "MM/dd/yyyy HH:mm")}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {note.note}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
              </Box>
            )}
          </CardContent>
        </Card>

        <Dialog open={holdModalOpen} onClose={() => setHoldModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Hold Disconnection</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Please provide a reason for holding this disconnection.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Hold Reason"
              value={holdNote}
              onChange={(e) => setHoldNote(e.target.value)}
              placeholder="Enter reason for holding this disconnection..."
              sx={{ mt: 2 }}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHoldModalOpen(false)}>Cancel</Button>
            <Button
              onClick={handleHoldSubmit}
              variant="contained"
              color="primary"
              disabled={!holdNote.trim()}
            >
              Hold
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={rescheduleModalOpen} onClose={() => setRescheduleModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Reschedule Disconnection</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Are you sure you want to reschedule this disconnect? This will update the manual hold date to the current date.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRescheduleModalOpen(false)}>Cancel</Button>
            <Button
              onClick={handleRescheduleSubmit}
              variant="contained"
              color="primary"
            >
              Reschedule
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={revertModalOpen} onClose={() => setRevertModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Revert Disconnection</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Are you sure you want to revert this disconnection to the previous stage?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRevertModalOpen(false)}>Cancel</Button>
            <Button
              onClick={handleRevertSubmit}
              variant="contained"
              color="primary"
            >
              Revert
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={noteModalOpen} onClose={() => setNoteModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add System Note</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Note Date"
              type="date"
              value={noteDate}
              onChange={(e) => setNoteDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Note"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Enter system note..."
              required
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Creator: {props.user?.details?.name || "Current User"}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNoteModalOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAddNote}
              variant="contained"
              color="primary"
              disabled={!newNote.trim()}
            >
              Add Note
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={closeModalOpen}
          onClose={() => setCloseModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Close Disconnect</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Are you sure you want to close this disconnection? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCloseModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCloseDisconnect} variant="contained" color="primary">
              Close Disconnect
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </NavFrame>
  );
};

DisconnectDetail.propTypes = {
  accountDisconnects: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  getAccountDisconnectForQueueByUuid: PropTypes.func.isRequired,
  createDisconnectHold: PropTypes.func.isRequired,
  deleteByUuid: PropTypes.func.isRequired,
  rescheduleAccountDisconnect: PropTypes.func.isRequired,
  markDisconnectAsPending: PropTypes.func.isRequired,
  resendDisconnectNotice: PropTypes.func.isRequired,
  revertAccountDisconnect: PropTypes.func.isRequired,
};

function DisconnectDetailState(state) {
  return {
    accountDisconnects: state.accountDisconnects,
    user: state.user,
  };
}

export default connect(DisconnectDetailState, {
  getAccountDisconnectForQueueByUuid,
  createDisconnectHold,
  rescheduleAccountDisconnect,
  markDisconnectAsPending,
  resendDisconnectNotice,
  revertAccountDisconnect,
  deleteByUuid,
})(DisconnectDetail);

